/**
 * Team invites and roles (XC-TEAM-INVITES Phase 1): the gateway's team and link shapes, checked
 * on arrival so the page never draws a reply off the contract
 * (`gateway_service/src/gateway/schemas/team.py`).
 */

import { z } from 'zod';

export const ROLES = ['owner', 'admin', 'member', 'viewer'] as const;
export const RoleSchema = z.enum(ROLES);
export type TeamRole = z.infer<typeof RoleSchema>;

export const TeamMemberSchema = z.object({
  user_id: z.string(),
  email: z.string(),
  display_name: z.string(),
  role: RoleSchema,
  is_active: z.boolean(),
  mfa_enrolled: z.boolean(),
  joined_at: z.string(),
  is_you: z.boolean(),
});
export type TeamMember = z.infer<typeof TeamMemberSchema>;

export const TeamInviteSchema = z.object({
  invite_id: z.string(),
  email: z.string(),
  role: RoleSchema,
  // withdrawn: its sender left or can no longer give its role; a manager's Resend sends it again.
  state: z.enum(['requested', 'sent', 'expired', 'withdrawn']),
  note: z.string().nullable(),
  invited_by: z.string(),
  invited_by_name: z.string().nullable(),
  requested_at: z.string(),
  sent_at: z.string().nullable(),
  expires_at: z.string().nullable(),
});
export type TeamInvite = z.infer<typeof TeamInviteSchema>;

export const TeamSchema = z.object({
  can_manage: z.boolean(),
  members: z.array(TeamMemberSchema),
  invited: z.array(TeamInviteSchema),
  requests: z.array(TeamInviteSchema),
});
export type Team = z.infer<typeof TeamSchema>;

export const InviteOutcomeSchema = z.object({
  email: z.string(),
  status: z.enum(['invited', 'requested', 'invalid', 'member', 'already_invited', 'just_sent']),
  invite_id: z.string().nullable().optional(),
  name: z.string().nullable().optional(),
});
export const InviteResultSchema = z.object({
  results: z.array(InviteOutcomeSchema),
  sent: z.number(),
  requested: z.number(),
});
export type InviteResult = z.infer<typeof InviteResultSchema>;

export const ActivityItemSchema = z.object({
  event_id: z.string(),
  act: z.string(),
  at: z.string(),
  actor_name: z.string().nullable(),
  subject_name: z.string().nullable(),
  email: z.string().nullable(),
  before: z.record(z.unknown()).nullable(),
  after: z.record(z.unknown()).nullable(),
});
export type ActivityItem = z.infer<typeof ActivityItemSchema>;
export const ActivitySchema = z.object({ items: z.array(ActivityItemSchema) });

/**
 * The team's use over time (XC-TEAM-ACTIVITY U1, `GET /api/v1/team/usage`, every role).
 * One value per bucket; a person's value is null for an empty bucket before the day they joined
 * (use dated earlier is kept as its number). Every series must be as long as the buckets, or the
 * reply is off its contract and nothing is drawn.
 */
export const USAGE_RANGES = [7, 30, 90] as const;
export type UsageRange = (typeof USAGE_RANGES)[number];

export const UsagePersonSchema = z.object({
  user_id: z.string(),
  display_name: z.string(),
  role: z.string(),
  is_active: z.boolean(),
  joined_on: z.string(),
  questions: z.array(z.number().nullable()),
  ai_usd: z.array(z.number().nullable()),
  ai_tokens: z.array(z.number().nullable()),
});
export type UsagePerson = z.infer<typeof UsagePersonSchema>;

export const TeamUsageSchema = z
  .object({
    days: z.union([z.literal(7), z.literal(30), z.literal(90)]),
    zone: z.string(),
    bucket: z.enum(['day', 'week']),
    buckets: z.array(z.object({ start: z.string(), end: z.string() })).min(1),
    people: z.array(UsagePersonSchema),
    people_cut: z.boolean(),
    /** AskWingo staff in this workspace (ruling G3): never a person, never the people's totals. */
    support: z.object({
      questions: z.array(z.number()),
      ai_usd: z.array(z.number()),
      ai_tokens: z.array(z.number()),
    }),
    other_accounts: z.object({
      questions: z.array(z.number()),
      ai_usd: z.array(z.number()),
      ai_tokens: z.array(z.number()),
    }),
    background: z.object({ ai_usd: z.array(z.number()), ai_tokens: z.array(z.number()) }),
    time_active_recorded: z.boolean(),
  })
  .superRefine((u, ctx) => {
    const n = u.buckets.length;
    const series: [string, unknown[]][] = [
      ['support.questions', u.support.questions],
      ['support.ai_usd', u.support.ai_usd],
      ['support.ai_tokens', u.support.ai_tokens],
      ['other_accounts.questions', u.other_accounts.questions],
      ['other_accounts.ai_usd', u.other_accounts.ai_usd],
      ['other_accounts.ai_tokens', u.other_accounts.ai_tokens],
      ['background.ai_usd', u.background.ai_usd],
      ['background.ai_tokens', u.background.ai_tokens],
      ...u.people.flatMap((p): [string, unknown[]][] => [
        [`people.${p.user_id}.questions`, p.questions],
        [`people.${p.user_id}.ai_usd`, p.ai_usd],
        [`people.${p.user_id}.ai_tokens`, p.ai_tokens],
      ]),
    ];
    for (const [name, values] of series) {
      if (values.length !== n) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: `${name} has ${values.length} values for ${n} buckets` });
      }
    }
  });
export type TeamUsage = z.infer<typeof TeamUsageSchema>;

export const LINK_STATES = ['live', 'invalid', 'expired', 'used', 'cancelled'] as const;
export type LinkState = (typeof LINK_STATES)[number];

export const InviteLinkSchema = z.object({
  state: z.enum(LINK_STATES),
  inviter_name: z.string().nullable().optional(),
  workspace_name: z.string().nullable().optional(),
  role: RoleSchema.nullable().optional(),
  email: z.string().nullable().optional(),
});
export type InviteLink = z.infer<typeof InviteLinkSchema>;

/** What each role may do, in one line (the invite window, the Team page, the email). */
export const ROLE_LINES: Record<TeamRole, string> = {
  owner: 'Everything an Admin can, plus making other Owners',
  admin: 'Runs the team, connections and rules; approves POs and emails. Sets up a two-step code.',
  member: "Uses the chat, answers questions, uploads files; can't approve or change settings",
  viewer: "Can look at everything, can't change anything",
};

export const ROLE_NAMES: Record<TeamRole, string> = {
  owner: 'Owner',
  admin: 'Admin',
  member: 'Member',
  viewer: 'Viewer',
};

/** The roles a viewer of this role may give (the server enforces the same ceilings). */
export function grantableRoles(viewerRole: TeamRole): TeamRole[] {
  if (viewerRole === 'owner') return [...ROLES];
  if (viewerRole === 'admin') return ['admin', 'member', 'viewer'];
  if (viewerRole === 'member') return ['member', 'viewer'];
  return [];
}

export const MAX_EMAILS_PER_INVITE = 20;
